import React, { useEffect, useState } from 'react';
import { uuid } from 'utils/dom';
import { i18n } from 'utils/i18n';
import TooltipTrigger from 'components/ToolTip/TooltipTrigger';
import ToolTipTemplate from 'components/ToolTip/ToolTipTemplate';

const CAPS_LOCK_KEY = 'CapsLock';
const TAB_KEY = 'Tab';

/**
 * Render Props component to show a tooltip informing the user when they have caps lock activated
 * @param {object} props - React Props
 * @param {function(props)} props.children - render props, children should be a function
 * @return {JSX} CapsLockControl React Component
 */
const CapsLockControl = ({ children }) => {
  const tooltipUniqueID = uuid();

  const [capsLockActive, setCapsLockActive] = useState(false);
  const tooltipID = `tooltip-id-${tooltipUniqueID}`;

  useEffect(() => {
    // For MS IE/Edge the native caps lock warning should be disabled
    document.msCapsLockWarningOff = true;
  }, []);

  // This checks for the specific caps lock keycode for when it's directly pressed.
  const isTabKey = (event) => event?.key === TAB_KEY;

  // This checks for the specific caps lock keycode for when it's directly pressed.
  const isCapsLockKey = (event) => event?.key === CAPS_LOCK_KEY;

  // This checks for the `CapsLock` modifier state, which is only available on mouse or keyboard events;
  const isCapsLockModifierOn = (event) => !!event?.getModifierState?.(CAPS_LOCK_KEY);

  /**
   * Pressing the caps lock key will only fire keyup if it is turned 'off',
   * so we can assume it to be false in case this fires with keycode === CAPS_LOCK_KEY_CODE.
   */
  const checkKeyUpEvent = (event) => {
    if (isCapsLockKey(event) || isTabKey(event)) {
      setCapsLockActive(isCapsLockModifierOn(event));
    }
  };

  /**
   * Pressing the caps lock key will only fire keydown if it is turned 'on'.
   * Also, since the focus doesn't contain the `getModifierState` function,
   * we check for the CapsLock Modifier State when the user presses any key.
   */
  const checkKeyDownEvent = (event) => {
    if (isCapsLockKey(event) || isCapsLockModifierOn(event)) {
      setCapsLockActive(isCapsLockModifierOn(event));
    }
  };

  // MouseDown has the modifier state getter, so we can verify it immediately when triggered.
  const checkOnFieldClick = (event) => {
    if (isCapsLockModifierOn(event)) {
      setCapsLockActive(true);
    }
  };

  // On Blur, we need to clean up the tooltip to avoid leaving it up when the field is not focused.
  const resetCapsLockState = () => {
    setCapsLockActive(false);
  };

  const capsLockCheckListeners = {
    onKeyUp: checkKeyUpEvent,
    onKeyDown: checkKeyDownEvent,
    onMouseDown: checkOnFieldClick,
    onBlur: resetCapsLockState,
  };

  const warningIcon = <span className='tooltips__warning-icon' />;

  return (
    <TooltipTrigger
      placement='top-start'
      // trigger='null' means we're manually controlling state with tooltipShown
      trigger={null}
      tooltipShown={capsLockActive}
      tooltip={ToolTipTemplate(warningIcon, i18n('field_password_caps_lock_on'), tooltipID)}
      tooltipID={tooltipID}
    >
      {({ getTriggerProps, triggerRef }) => (
        // we need this wrapper and its' anchor child to properly position the arrow in relation to the field
        <div className='tooltips__caps-lock-wrapper'>
          <span
            {...getTriggerProps({
              className: 'tooltips__caps-lock-anchor',
              ref: triggerRef,
            })}
          />
          {children(capsLockCheckListeners)}
        </div>
      )}
    </TooltipTrigger>
  );
};

export default CapsLockControl;
